type OpenModalOptions = {
    id: string;
    modalType: 'video';
    template: string;
    noPadding?: boolean;
};

type ModalOptions = Partial<OpenModalOptions>;

class Modal {
    #modals: HTMLDialogElement[] = [];

    #createModalElm(options: Pick<OpenModalOptions, 'id' | 'modalType' | 'noPadding'>) {
        const { id, modalType, noPadding } = options;

        const modalElm = document.createElement('dialog') as HTMLDialogElement;
        modalElm.setAttribute('id', id);
        modalElm.setAttribute('role', 'dialog');
        modalElm.setAttribute('data-component', 'modal');

        modalElm.classList.add('modal', `modal-type-${modalType}`);
        if (noPadding) {
            modalElm.classList.add('modal-no-padding');
        }

        this.#modals.push(modalElm);
        document.body.appendChild(modalElm);

        // Setup dialog events.
        this.#setupModalEvents(modalElm);

        return modalElm;
    }

    #cleanupModalElm(modalElm: HTMLDialogElement) {
        this.#modals = this.#modals.filter((modal) => modal !== modalElm);
        modalElm.remove();
    }

    #setupModalEvents(modalElm: HTMLDialogElement) {
        // Makes sure to remove modal from list and the dom when closed.
        modalElm.addEventListener(
            'close',
            (_) => {
                this.#cleanupModalElm(modalElm);
            },
            { once: true }
        );
        // Close modal when pressing escape.
        modalElm.addEventListener(
            'keydown',
            (e) => {
                if (e.key === 'Escape') {
                    modalElm.close();
                }
            },
            { once: true }
        );
    }

    #setupModalContent(modalElm: HTMLDialogElement, template: string) {
        const modalHtml = `
            <div class="modal-content">
                ${template}
            </div>
            <div class="modal-close-btn-container">
                <button class="modal-close-btn" aria-label="Close modal">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        `;
        modalElm.innerHTML = modalHtml;

        // Close modal when clicking on the close button.
        const closeBtn = modalElm.querySelector<HTMLElement>('.modal-close-btn');
        console.log('FOUND BTN: ', closeBtn);
        if (closeBtn) {
            closeBtn.addEventListener(
                'click',
                (_) => {
                    console.log('CLOSE!');
                    modalElm.close();
                },
                { once: true }
            );
        }
    }

    #openModal(options: OpenModalOptions) {
        const { template } = options;

        const modalElm = this.#createModalElm({ ...options });
        this.#setupModalContent(modalElm, template);
        modalElm.showModal();
    }

    openVideoModal(videoData: { src: string; type?: string }, options?: ModalOptions) {
        const template = `
            <div class="modal-video-container">
                <video controls autoplay>
                    <source src="${videoData.src}" type="${videoData.type || 'video/mp4'}">
                </video>
            </div>`;

        return this.#openModal({ id: 'video-modal', modalType: 'video', template, noPadding: true, ...options });
    }

    closeAll() {
        this.#modals.forEach((modal) => {
            modal.close();
        });
    }

    close(id: string) {
        this.#modals.find((modal) => modal.id === id)?.close();
    }
}

export default Modal;
